import { DateTime } from 'luxon';
import { parseRideMaintenanceSchedule } from 'public/components/AttractionScreen/RideClosures/utils';

/**
 * Ride maintenance items affecting a given date.
 *
 * Shared core extracted from OperationalHoursModal.getTemporaryClosedInfo so
 * the checkout calendar, the CLP modal and the closures detail modal all build
 * on the same filtering. Returns the raw maintenance items (with image, name,
 * reason, startDate, endDate) for that day.
 *
 * @param {Array} ridesCards - parsed ride cards (see getContentRides)
 * @param {Date} date - JS Date of the selected day
 * @returns {Array}
 */
const getClosureItemsForDate = (ridesCards = [], date) => {
  if (!date || !(date instanceof Date)) return [];

  const maintenanceSchedule = parseRideMaintenanceSchedule(
    (ridesCards || []).map((ride) => ({
      ...ride,
      schedule: {
        ...ride.schedule,
        closedDates:
          ride.schedule?.closedDates?.map((closedDate) => ({
            ...closedDate,
            startDate: DateTime.fromISO(closedDate.startDate),
            endDate: DateTime.fromISO(closedDate.endDate),
            reason: closedDate.reason || '',
          })) || [],
      },
    }))
  );

  const selectedDateTime = DateTime.fromJSDate(date).startOf('day');

  return maintenanceSchedule
    .flatMap((maintenance) => maintenance.items)
    .filter((item) => {
      if (!item.startDate) return true;

      const startDate =
        item.startDate instanceof DateTime
          ? item.startDate
          : DateTime.fromISO(item.startDate);

      const endDate =
        item.endDate instanceof DateTime
          ? item.endDate
          : DateTime.fromISO(item.endDate);

      return selectedDateTime >= startDate && selectedDateTime <= endDate;
    });
};

/**
 * Toggle/indicator summary of the closures affecting a date.
 *
 * @returns {{ isClosed: boolean, message: string } | null}
 */
export const getClosuresForDate = (ridesCards = [], date) => {
  const relevantItems = getClosureItemsForDate(ridesCards, date);

  if (relevantItems.length === 0) return null;

  const message = relevantItems
    .map((item) => `• ${item.name.trim()}: ${item.reason.trim()}`)
    .join('\n');

  return { isClosed: true, message };
};

/**
 * Rich list of closures for the detail modal: image, name and date range,
 * mirroring the character appearances modal layout.
 *
 * @returns {Array<{ image: string, alt: string, name: string, dateRange: string }>}
 */
export const getClosureDetailsForDate = (ridesCards = [], date) => {
  return getClosureItemsForDate(ridesCards, date).map((item) => {
    const start =
      item.startDate instanceof DateTime
        ? item.startDate
        : item.startDate && DateTime.fromISO(item.startDate);
    const end =
      item.endDate instanceof DateTime
        ? item.endDate
        : item.endDate && DateTime.fromISO(item.endDate);

    return {
      image: item.image?.assetUrl,
      alt: item.image?.assetAlt || item.name,
      name: item.name,
      // e.g. "Jun 26 - Jul 06, 2026" (year shown once, on the end date).
      dateRange:
        start && end
          ? `${start.toFormat('LLL dd')} - ${end.toFormat('LLL dd, yyyy')}`
          : '',
    };
  });
};
